import { c as _c } from "react/compiler-runtime";
import { useViewPort } from "@website/hooks/viewport";
import { MaterialIcon } from "@website/icons/material";
import { FC, ReactElement, ReactNode, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowContainer, Popover, PopoverProps } from "react-tiny-popover";
import { getPopoverViewportOffset } from "./helpers";
type NewPopoverProperties = {
  panel: ReactNode;
  title?: ReactNode;
  className?: string;
  containerClassName?: string;
  children: ReactElement;
  isOpen?: boolean;
  align?: PopoverProps["align"];
  positions?: PopoverProps["positions"];
  onOpenChange?: (isOpen: boolean) => void;
  mode?: "menu" | "tooltip";
  arrowColor?: string;
};
const NewPopover: FC<NewPopoverProperties> = t0 => {
  const $ = _c(39);
  const {
    title,
    panel,
    className: t1,
    containerClassName: t2,
    children,
    isOpen: controlledIsOpen,
    align: t3,
    positions: t4,
    onOpenChange,
    mode: t5,
    arrowColor: t6
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const containerClassName = t2 === undefined ? "" : t2;
  const align = t3 === undefined ? "center" : t3;
  const positions = t4 === undefined ? "bottom" : t4;
  const mode = t5 === undefined ? "menu" : t5;
  const arrowColor = t6 === undefined ? "var(--color-on-surface-medium-emphasis)" : t6;
  const {
    isMobile
  } = useViewPort();
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [boundaryElement, setBoundaryElement] = useState(null);
  const isControlled = controlledIsOpen !== undefined;
  const isPopoverOpen = isControlled ? controlledIsOpen : internalIsOpen;
  let t7;
  if ($[0] !== isControlled || $[1] !== onOpenChange) {
    t7 = open => {
      if (isControlled) {
        onOpenChange?.(open);
      } else {
        setInternalIsOpen(open);
        onOpenChange?.(open);
      }
    };
    $[0] = isControlled;
    $[1] = onOpenChange;
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  const setIsPopoverOpen = t7;
  let t8;
  bb0: {
    if (mode === "tooltip") {
      const t9 = boundaryElement || undefined;
      let t10;
      if ($[3] !== t9) {
        t10 = {
          boundaryElement: t9,
          boundaryInset: 8,
          transformMode: "relative",
          transform: getPopoverViewportOffset
        };
        $[3] = t9;
        $[4] = t10;
      } else {
        t10 = $[4];
      }
      t8 = t10;
      break bb0;
    }
    if (isMobile) {
      let t9;
      if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = {
          transformMode: "absolute",
          transform: {
            top: 0,
            left: 0
          }
        };
        $[5] = t9;
      } else {
        t9 = $[5];
      }
      t8 = t9;
      break bb0;
    }
    let t9;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = {};
      $[6] = t9;
    } else {
      t9 = $[6];
    }
    t8 = t9;
  }
  const options = t8;
  let t9;
  if ($[7] !== isPopoverOpen || $[8] !== mode) {
    t9 = mode === "tooltip" && isPopoverOpen && typeof document !== "undefined" && createPortal(<div aria-hidden={true} ref={setBoundaryElement} style={{
      position: "fixed",
      inset: 0,
      visibility: "hidden",
      pointerEvents: "none"
    }} />, document.body);
    $[7] = isPopoverOpen;
    $[8] = mode;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const t10 = `z-[1000] ${isMobile && mode === "menu" ? "!translate-y-0 w-screen h-screen" : ""} ${containerClassName}`;
  let t11;
  if ($[10] !== arrowColor || $[11] !== className || $[12] !== isMobile || $[13] !== mode || $[14] !== panel || $[15] !== setIsPopoverOpen || $[16] !== title) {
    t11 = t12 => {
      const {
        position,
        childRect,
        popoverRect,
        transform
      } = t12;
      return mode === "tooltip" ? <ArrowContainer position={position} childRect={childRect} popoverRect={{
        ...popoverRect,
        left: popoverRect.left + (transform?.left || 0),
        right: popoverRect.right + (transform?.left || 0),
        top: popoverRect.top + (transform?.top || 0),
        bottom: popoverRect.bottom + (transform?.top || 0)
      }} arrowSize={5} arrowColor={arrowColor}><div className={className}>{panel}</div></ArrowContainer> : <div className={`elevation-4 lg:elevation-8 fixed bottom-0 top-auto z-[1000] h-auto w-screen rounded-lg lg:static lg:w-auto ${className}`}><div className="flex justify-end md:mt-0">{isMobile && title ? <div className="text-on-surface-high-emphasis leading-12 flex h-12 flex-1 items-center justify-start pr-3 text-lg font-semibold">{title}</div> : null}<div className="flex size-10 items-center justify-center lg:hidden"><MaterialIcon className="text-2xl" name="close" onClick={() => setIsPopoverOpen(false)} /></div></div>{panel}</div>;
    };
    $[10] = arrowColor;
    $[11] = className;
    $[12] = isMobile;
    $[13] = mode;
    $[14] = panel;
    $[15] = setIsPopoverOpen;
    $[16] = title;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== setIsPopoverOpen) {
    t12 = () => setIsPopoverOpen(false);
    $[18] = setIsPopoverOpen;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== isPopoverOpen || $[21] !== mode || $[22] !== setIsPopoverOpen) {
    t13 = mode === "menu" ? () => setIsPopoverOpen(!isPopoverOpen) : undefined;
    $[20] = isPopoverOpen;
    $[21] = mode;
    $[22] = setIsPopoverOpen;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== children || $[25] !== t13) {
    t14 = <div onClick={t13}>{children}</div>;
    $[24] = children;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== align || $[28] !== isPopoverOpen || $[29] !== options || $[30] !== positions || $[31] !== t10 || $[32] !== t11 || $[33] !== t12 || $[34] !== t14) {
    t15 = <Popover {...options} align={align} containerClassName={t10} content={t11} isOpen={isPopoverOpen} onClickOutside={t12} padding={4} positions={positions}>{t14}</Popover>;
    $[27] = align;
    $[28] = isPopoverOpen;
    $[29] = options;
    $[30] = positions;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t15 || $[37] !== t9) {
    t16 = <>{t9}{t15}</>;
    $[36] = t15;
    $[37] = t9;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  return t16;
};
export { NewPopover };